import { useState } from 'react';
import { DatabaseEvent } from '../types/types';

/**
 * Defines the structure of an event tag.
 *
 * - name: Label used to categorize the event.
 * - color: Hex color code associated with the tag.
 */
type EventTag = {
  name: string;
  color: string;
};

/**
 * Custom React hook for managing and filtering event tags.
 *
 * Provides a list of predefined tags, tracks the selected tag,
 * and includes a method to filter events by the currently selected tag.
 *
 * @returns An object containing:
 * - eventTags: Predefined list of available tags.
 * - selectedTag: Currently selected tag (or null if none).
 * - setSelectedTag: Function to update the selected tag.
 * - filterEventsByTag: Function to filter a list of events by the selected tag.
 */
export const useEventTags = () => {
  const eventTags: EventTag[] = [
    { name: 'Watch Party', color: '#ff6347' },
    { name: 'Q+A', color: '#ffa500' },
    { name: 'Task', color: '#32cd32' },
    { name: 'Game', color: '#1e90ff' },
    { name: 'Misc', color: '#800080' },
  ];

  // Manage selected tag
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  /**
   * Filters a list of events by the currently selected tag.
   *
   * @param events Array of events to filter.
   * @returns Filtered array of events if a tag is selected, otherwise the original array.
   */
  const filterEventsByTag = (events: DatabaseEvent[]) => {
    if (!selectedTag) return events;
    return events.filter(event => event.tag === selectedTag);
  };

  return {
    eventTags,
    selectedTag,
    setSelectedTag,
    filterEventsByTag,
  };
};

export default useEventTags;
